import { useEffect, useRef, useState, useCallback } from 'react';
import { useConnectionStatusOptional } from '../contexts/ConnectionStatusContext';
import { fetchWithAuth } from '../services/api';

export interface AccessEvent {
  id: string;
  customer_id: string;
  device_id: string;
  device_name?: string;
  person_id?: string;
  person_name: string;
  employee_no: string;
  is_authorized: boolean;
  event_type: string;
  event_timestamp: string;      // When event occurred (device time)
  processed_at: string;          // When backend received it (server time)
  face_photo_url?: string;
  zone_name?: string;
  direction?: string;  // 'entrada', 'saida', 'passagem'
  event_category?: string;
  severity?: string;
  message?: string;             // Event message/summary
  description?: string;         // Detailed event description
  status?: string;              // Event status (success, error, etc.)
  person_type?: string;         // Person role type (visitor, resident, etc.)
  triggered_by_name?: string;
  triggered_by_email?: string;
  triggered_by_user_id?: string;
}

interface UseEventStreamOptions {
  customerId: string;
  token?: string;  // Deprecated: token is now managed by fetchWithAuth
  enabled?: boolean;
  onEvent?: (event: AccessEvent) => void;
  onError?: (error: Error) => void;
}

// Heartbeat timeout: if no heartbeat received in this time, consider disconnected
// Backend sends heartbeat every 15s, so we use 35s (2x + buffer)
const HEARTBEAT_TIMEOUT_MS = 35000;

// Reconnection settings with exponential backoff
// Minimum 3 seconds between reconnection attempts to avoid flooding on 401 errors
const INITIAL_RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_DELAY_MS = 30000;
const RECONNECT_BACKOFF_MULTIPLIER = 2;

export const useEventStream = ({
  customerId,
  token: _token,  // Deprecated: token is now managed by fetchWithAuth
  enabled = true,
  onEvent,
  onError
}: UseEventStreamOptions) => {
  // Note: _token is kept for backward compatibility but not used
  // Authentication is handled by fetchWithAuth which gets token from api.ts
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [latestEvent, setLatestEvent] = useState<AccessEvent | null>(null);

  // Deduplication: Track seen event IDs with sliding window (last 100 events)
  // This prevents duplicate events from appearing in UI even if backend sends them
  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const MAX_SEEN_EVENTS = 100;

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  // Track when connection became active — used to distinguish server-timeout drops from real errors
  const connectedAtRef = useRef<number | null>(null);

  // Track current connection ID to prevent stale handlers from reconnecting
  const connectionIdRef = useRef(0);

  // Store callbacks in refs to avoid dependency issues
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);

  // Store customerId in ref for use in callbacks
  const customerIdRef = useRef(customerId);

  // Update refs when callbacks change
  useEffect(() => {
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
  }, [onEvent, onError]);

  // Update customerId ref when it changes
  useEffect(() => {
    customerIdRef.current = customerId;
  }, [customerId]);

  // Use connection status context (returns undefined if not wrapped in provider)
  const connectionStatus = useConnectionStatusOptional();
  const connectionStatusRef = useRef(connectionStatus);

  // Update ref when context changes
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // Cleanup function to properly close connection and clear timeouts
  const cleanup = useCallback(() => {
    console.log('SSE: Cleaning up connection');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Connect function using fetch with Authorization header
  const connect = useCallback(() => {
    if (!enabled || !customerId) {
      console.log('SSE: Not connecting - enabled:', enabled, 'customerId:', customerId);
      return;
    }

    // Increment connection ID to invalidate any previous connection's handlers
    const currentConnectionId = ++connectionIdRef.current;
    console.log('SSE: Starting new connection, id:', currentConnectionId);

    connectionStatusRef.current?.updateConnectionState('connecting');

    // Clean up any existing connection first
    cleanup();

    // Helper to check if this connection is still valid
    const isValidConnection = () => connectionIdRef.current === currentConnectionId;

    // Helper to reset heartbeat timeout
    const resetHeartbeat = () => {
      if (!isValidConnection()) return;

      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }
      heartbeatTimeoutRef.current = setTimeout(() => {
        if (!isValidConnection()) return;

        console.warn('SSE: No heartbeat received in', HEARTBEAT_TIMEOUT_MS, 'ms - connection may be stale');
        setIsConnected(false);
        connectionStatusRef.current?.updateConnectionState('disconnected');
        connectionStatusRef.current?.recordError('Conexão inativa - sem heartbeat');

        // Abort the current connection
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }

        // Schedule reconnection
        scheduleReconnectInternal();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    // Helper to schedule reconnection with exponential backoff
    const scheduleReconnectInternal = () => {
      if (!isValidConnection()) {
        console.log('SSE: Skipping reconnect - connection no longer valid');
        return;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      const delay = reconnectDelayRef.current;
      console.log(`SSE: Scheduling reconnect in ${delay}ms`);

      connectionStatusRef.current?.incrementReconnectAttempts();
      connectionStatusRef.current?.updateConnectionState('reconnecting');

      reconnectTimeoutRef.current = setTimeout(() => {
        if (!isValidConnection()) {
          console.log('SSE: Skipping reconnect - connection no longer valid');
          return;
        }

        // Increase delay for next attempt (exponential backoff)
        reconnectDelayRef.current = Math.min(
          reconnectDelayRef.current * RECONNECT_BACKOFF_MULTIPLIER,
          MAX_RECONNECT_DELAY_MS
        );

        // Use the current customerId from ref (might have changed)
        if (customerIdRef.current && enabled) {
          connect();
        }
      }, delay);
    };

    // Parse SSE data from text chunk
    const parseSSEMessage = (text: string): { event?: string; data?: string } | null => {
      const lines = text.split('\n');
      let event: string | undefined;
      let data: string | undefined;

      for (const line of lines) {
        if (line.startsWith('event:')) {
          event = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          data = line.slice(5).trim();
        }
      }

      if (event || data) {
        return { event, data };
      }
      return null;
    };

    // Start fetch-based SSE connection using fetchWithAuth for automatic token handling
    const startConnection = async () => {
      try {
        // Use CONDO_MANAGER_URL for consistency with api.ts
        // fetchWithAuth will handle the URL resolution, so we use a relative path
        const url = `/api/v1/events/stream?customer_id=${customerId}`;

        console.log('SSE: Connecting to:', url);

        // Create abort controller for this connection
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Use fetchWithAuth which automatically handles:
        // - Adding Authorization header from stored token
        // - Refreshing token on 401 and retrying
        const response = await fetchWithAuth(url, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: abortController.signal,
        });

        // Check if connection is still valid after await
        if (!isValidConnection()) {
          console.log('SSE: Connection invalidated during fetch');
          return;
        }

        // Handle HTTP errors
        if (!response.ok) {
          const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
          console.error('SSE: Connection failed -', errorMsg);

          setIsConnected(false);
          const error = new Error(errorMsg);
          setError(error);
          onErrorRef.current?.(error);

          connectionStatusRef.current?.updateConnectionState('disconnected');
          connectionStatusRef.current?.recordError(errorMsg);

          // Schedule reconnection with delay (important for 401 errors to avoid flooding)
          scheduleReconnectInternal();
          return;
        }

        // Connection successful
        console.log('SSE: Connection opened');
        setIsConnected(true);
        setError(null);
        connectedAtRef.current = Date.now();

        // Clear seen events on reconnect (fresh start)
        // This ensures we don't miss events after reconnection
        seenEventIdsRef.current.clear();
        console.log('SSE: Cleared event deduplication cache');

        // Reset reconnect delay on successful connection
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;

        // Update context
        connectionStatusRef.current?.updateConnectionState('connected');
        connectionStatusRef.current?.resetReconnectAttempts();

        // Start heartbeat monitoring
        resetHeartbeat();

        // Read the stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('Response body is not readable');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (!isValidConnection()) {
            console.log('SSE: Connection invalidated during read');
            reader.cancel();
            return;
          }

          if (done) {
            console.log('SSE: Stream ended');
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Process complete messages (separated by double newlines)
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || ''; // Keep incomplete message in buffer

          for (const messageText of messages) {
            if (!messageText.trim()) continue;

            const message = parseSSEMessage(messageText);
            if (!message) continue;

            // Handle different event types
            if (message.event === 'connected') {
              console.log('SSE: Connection confirmed:', message.data);
              connectionStatusRef.current?.recordHeartbeat();
              resetHeartbeat();
            } else if (message.event === 'heartbeat') {
              connectionStatusRef.current?.recordHeartbeat();
              resetHeartbeat();
              setIsConnected(true);
              connectionStatusRef.current?.updateConnectionState('connected');
            } else if (message.event === 'access_event' && message.data) {
              try {
                const event: AccessEvent = JSON.parse(message.data);

                // Deduplication check: Have we seen this event ID before?
                if (seenEventIdsRef.current.has(event.id)) {
                  console.warn('SSE: Duplicate event detected and ignored:', event.id);
                  resetHeartbeat();
                  continue; // Skip duplicate
                }

                // Add to seen events set
                seenEventIdsRef.current.add(event.id);

                // Maintain sliding window (keep only last 100 events)
                if (seenEventIdsRef.current.size > MAX_SEEN_EVENTS) {
                  // Remove oldest entry (first item in set)
                  const firstKey = seenEventIdsRef.current.values().next().value;
                  if (firstKey) {
                    seenEventIdsRef.current.delete(firstKey);
                  }
                }

                console.log('SSE: New access event received:', event);
                setLatestEvent(event);
                onEventRef.current?.(event);
                resetHeartbeat();
              } catch (err) {
                console.error('SSE: Failed to parse event:', err);
              }
            }
          }
        }

        // Stream ended normally — server closed the connection (expected server-timeout)
        if (isValidConnection()) {
          const connectedDuration = connectedAtRef.current ? Date.now() - connectedAtRef.current : 0;
          setIsConnected(false);
          connectedAtRef.current = null;

          if (connectedDuration > 15000) {
            // Was alive > 15s → treat as expected server-timeout: skip 'disconnected', go straight to reconnecting
            console.log('SSE: Stream closed after', connectedDuration, 'ms — silent reconnect (server timeout)');
          } else {
            // Short-lived connection that ended — show as disconnected
            connectionStatusRef.current?.updateConnectionState('disconnected');
          }
          scheduleReconnectInternal();
        }

      } catch (err) {
        // Ignore abort errors (expected when cleaning up)
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('SSE: Connection aborted');
          return;
        }

        if (!isValidConnection()) {
          console.log('SSE: Ignoring error from stale connection');
          return;
        }

        const error = err instanceof Error ? err : new Error('Erro desconhecido');
        const connectedDuration = connectedAtRef.current ? Date.now() - connectedAtRef.current : 0;
        // If we were connected for >15s and got a network-level error, treat it as a
        // server-timeout (proxy closed the connection) — reconnect silently
        const isSilentReconnect = connectedDuration > 15000 && err instanceof TypeError;
        connectedAtRef.current = null;

        console.error('SSE: Connection error:', error.message, isSilentReconnect ? '(silent reconnect)' : '');
        setIsConnected(false);

        if (!isSilentReconnect) {
          setError(error);
          onErrorRef.current?.(error);
          connectionStatusRef.current?.updateConnectionState('disconnected');
          connectionStatusRef.current?.recordError(error.message);
        }

        // Clear heartbeat timeout
        if (heartbeatTimeoutRef.current) {
          clearTimeout(heartbeatTimeoutRef.current);
          heartbeatTimeoutRef.current = null;
        }

        // Schedule reconnection
        scheduleReconnectInternal();
      }
    };

    startConnection();
  }, [customerId, enabled, cleanup]); // token is managed by fetchWithAuth

  // Manual reconnect function
  const reconnect = useCallback(() => {
    console.log('SSE: Manual reconnect requested');
    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
    connectionStatusRef.current?.resetReconnectAttempts();
    connect();
  }, [connect]);

  // Disconnect function
  const disconnect = useCallback(() => {
    console.log('SSE: Manual disconnect requested');

    // Increment connection ID to invalidate any pending reconnects
    connectionIdRef.current++;

    cleanup();
    setIsConnected(false);
    connectionStatusRef.current?.updateConnectionState('disconnected');
  }, [cleanup]);

  // Connect on mount and when dependencies change
  useEffect(() => {
    connect();

    return () => {
      // Increment connection ID to invalidate all handlers from this connection
      connectionIdRef.current++;
      cleanup();
    };
  }, [connect, cleanup]);

  return {
    isConnected,
    error,
    latestEvent,
    reconnect,
    disconnect,
  };
};
